/**
 * Code-level analysis components: Tanner Graph, Check Matrix, [[n,k,d]] parameters.
 * These appear in the left panel (StabilizerToolbar), not the right circuit-analysis panel.
 */
import { useState, useMemo, useCallback, useEffect } from 'react';
import { StabilizerCode } from '../../types/stabilizer';
import {
  buildParityMatrix,
  rowEchelon,
  computeCodeDistance,
  BitMatrix,
} from '../../utils/stabilizerAnalysis';

// ── Tanner Graph (interactive bipartite graph) ──

const TANNER_COLORS = {
  xErr: '#ef4444', zErr: '#a855f7', xStab: '#8b5cf6', zStab: '#3b82f6',
  edge: '#475569', edgeActive: '#fbbf24', flipped: '#f59e0b',
};

export function TannerGraphSection({
  code,
  onFlippedStabilizersChange,
}: {
  code: StabilizerCode;
  onFlippedStabilizersChange?: (ids: Set<string>) => void;
}) {
  const [xErrors, setXErrors] = useState<Set<number>>(new Set());
  const [zErrors, setZErrors] = useState<Set<number>>(new Set());

  const n = code.qubits.length;
  const qubitIds = code.qubits.map((q) => q.id);
  const xStabs = useMemo(() => code.stabilizers.filter((s) => s.type === 'X'), [code.stabilizers]);
  const zStabs = useMemo(() => code.stabilizers.filter((s) => s.type === 'Z'), [code.stabilizers]);
  const idxMap = useMemo(() => new Map(qubitIds.map((id, i) => [id, i])), [qubitIds]);

  // Z stabs detect X errors
  const zSyndromes = useMemo(() => {
    return zStabs.map((s) => {
      let parity = 0;
      for (const qid of s.qubitIds) {
        const idx = idxMap.get(qid);
        if (idx !== undefined && xErrors.has(idx)) parity ^= 1;
      }
      return parity === 1;
    });
  }, [zStabs, xErrors, idxMap]);

  // X stabs detect Z errors
  const xSyndromes = useMemo(() => {
    return xStabs.map((s) => {
      let parity = 0;
      for (const qid of s.qubitIds) {
        const idx = idxMap.get(qid);
        if (idx !== undefined && zErrors.has(idx)) parity ^= 1;
      }
      return parity === 1;
    });
  }, [xStabs, zErrors, idxMap]);

  // Notify parent of flipped stabilizer IDs
  useEffect(() => {
    if (!onFlippedStabilizersChange) return;
    const flipped = new Set<string>();
    zStabs.forEach((s, i) => { if (zSyndromes[i]) flipped.add(s.id); });
    xStabs.forEach((s, i) => { if (xSyndromes[i]) flipped.add(s.id); });
    onFlippedStabilizersChange(flipped);
  }, [zSyndromes, xSyndromes, zStabs, xStabs, onFlippedStabilizersChange]);

  const toggleX = (qi: number) => {
    setXErrors((prev) => { const s = new Set(prev); s.has(qi) ? s.delete(qi) : s.add(qi); return s; });
  };
  const toggleZ = (qi: number) => {
    setZErrors((prev) => { const s = new Set(prev); s.has(qi) ? s.delete(qi) : s.add(qi); return s; });
  };

  if (n === 0) return null;

  const leftX = 30, midX = 170, rightX = 330, nodeR = 14, gapY = 38, topPad = 30;
  const stabGap = 24;
  const svgW = 400;
  const leftH = n * gapY;
  const midH = (zStabs.length + xStabs.length) * gapY + stabGap;
  const svgH = Math.max(leftH, midH) + topPad * 2;

  return (
    <div className="space-y-2">
      <p className="text-[10px] text-gray-600">Click qubit nodes to toggle errors. Z stabilizers detect X errors, X stabilizers detect Z errors.</p>
      <div className="overflow-auto border border-gray-800 rounded bg-gray-950">
        <svg width={svgW} height={svgH}>
          <text x={leftX} y={14} fontSize={9} fill={TANNER_COLORS.xErr} fontWeight="bold" fontFamily="monospace">X errors</text>
          <text x={midX - 10} y={14} fontSize={9} fill="#64748b" fontWeight="bold" fontFamily="monospace">Stabilizers</text>
          <text x={rightX - 5} y={14} fontSize={9} fill={TANNER_COLORS.zErr} fontWeight="bold" fontFamily="monospace">Z errors</text>

          {/* Edges: X error qubits → Z stabilizers */}
          {zStabs.map((s, si) => {
            const sy = topPad + si * gapY + gapY / 2;
            return s.qubitIds.map((qid) => {
              const qi = idxMap.get(qid);
              if (qi === undefined) return null;
              const qy = topPad + qi * gapY + gapY / 2;
              const active = xErrors.has(qi);
              return (
                <line key={`xe-${qi}-${si}`}
                  x1={leftX + nodeR + 2} y1={qy} x2={midX - 24} y2={sy}
                  stroke={active ? TANNER_COLORS.edgeActive : TANNER_COLORS.edge}
                  strokeWidth={active ? 2 : 1} opacity={active ? 0.8 : 0.2} />
              );
            });
          })}

          {/* Edges: Z error qubits → X stabilizers */}
          {xStabs.map((s, si) => {
            const sy = topPad + zStabs.length * gapY + stabGap + si * gapY + gapY / 2;
            return s.qubitIds.map((qid) => {
              const qi = idxMap.get(qid);
              if (qi === undefined) return null;
              const qy = topPad + qi * gapY + gapY / 2;
              const active = zErrors.has(qi);
              return (
                <line key={`ze-${qi}-${si}`}
                  x1={rightX - nodeR - 2} y1={qy} x2={midX + 24} y2={sy}
                  stroke={active ? TANNER_COLORS.edgeActive : TANNER_COLORS.edge}
                  strokeWidth={active ? 2 : 1} opacity={active ? 0.8 : 0.2} />
              );
            });
          })}

          {/* X error qubit nodes (left) */}
          {Array.from({ length: n }, (_, qi) => {
            const y = topPad + qi * gapY + gapY / 2;
            const active = xErrors.has(qi);
            return (
              <g key={`xq-${qi}`} className="cursor-pointer" onClick={() => toggleX(qi)}>
                <circle cx={leftX} cy={y} r={nodeR}
                  fill={active ? TANNER_COLORS.xErr : '#1e293b'}
                  stroke={TANNER_COLORS.xErr} strokeWidth={active ? 2.5 : 1.5} />
                <text x={leftX} y={y + 1} textAnchor="middle" dominantBaseline="middle"
                  fontSize={9} fill={active ? 'white' : '#9ca3af'} fontFamily="monospace" fontWeight="bold">
                  {qi}
                </text>
              </g>
            );
          })}

          {/* Z error qubit nodes (right) */}
          {Array.from({ length: n }, (_, qi) => {
            const y = topPad + qi * gapY + gapY / 2;
            const active = zErrors.has(qi);
            return (
              <g key={`zq-${qi}`} className="cursor-pointer" onClick={() => toggleZ(qi)}>
                <circle cx={rightX} cy={y} r={nodeR}
                  fill={active ? TANNER_COLORS.zErr : '#1e293b'}
                  stroke={TANNER_COLORS.zErr} strokeWidth={active ? 2.5 : 1.5} />
                <text x={rightX} y={y + 1} textAnchor="middle" dominantBaseline="middle"
                  fontSize={9} fill={active ? 'white' : '#9ca3af'} fontFamily="monospace" fontWeight="bold">
                  {qi}
                </text>
              </g>
            );
          })}

          {/* Z stabilizer nodes (middle-top) */}
          {zStabs.map((s, si) => {
            const y = topPad + si * gapY + gapY / 2;
            const flipped = zSyndromes[si];
            const label = s.name || `Z${si}`;
            return (
              <g key={`zs-${si}`}>
                {flipped && <rect x={midX - 26} y={y - nodeR - 3} width={52} height={(nodeR + 3) * 2}
                  rx={6} fill={TANNER_COLORS.flipped} opacity={0.25}>
                  <animate attributeName="opacity" values="0.15;0.35;0.15" dur="1s" repeatCount="indefinite" />
                </rect>}
                <rect x={midX - 22} y={y - nodeR} width={44} height={nodeR * 2} rx={4}
                  fill={flipped ? TANNER_COLORS.flipped : '#0f172a'}
                  stroke={flipped ? '#fbbf24' : TANNER_COLORS.zStab} strokeWidth={flipped ? 2.5 : 1.5} />
                <text x={midX} y={y + 1} textAnchor="middle" dominantBaseline="middle"
                  fontSize={8} fill={flipped ? '#000' : TANNER_COLORS.zStab} fontFamily="monospace" fontWeight="bold">
                  {label}
                </text>
              </g>
            );
          })}

          {zStabs.length > 0 && xStabs.length > 0 && (
            <line x1={midX - 30} y1={topPad + zStabs.length * gapY + stabGap / 2}
              x2={midX + 30} y2={topPad + zStabs.length * gapY + stabGap / 2}
              stroke="#334155" strokeWidth={1} strokeDasharray="4 2" />
          )}

          {/* X stabilizer nodes (middle-bottom) */}
          {xStabs.map((s, si) => {
            const y = topPad + zStabs.length * gapY + stabGap + si * gapY + gapY / 2;
            const flipped = xSyndromes[si];
            const label = s.name || `X${si}`;
            return (
              <g key={`xs-${si}`}>
                {flipped && <rect x={midX - 26} y={y - nodeR - 3} width={52} height={(nodeR + 3) * 2}
                  rx={6} fill={TANNER_COLORS.flipped} opacity={0.25}>
                  <animate attributeName="opacity" values="0.15;0.35;0.15" dur="1s" repeatCount="indefinite" />
                </rect>}
                <rect x={midX - 22} y={y - nodeR} width={44} height={nodeR * 2} rx={4}
                  fill={flipped ? TANNER_COLORS.flipped : '#0f172a'}
                  stroke={flipped ? '#fbbf24' : TANNER_COLORS.xStab} strokeWidth={flipped ? 2.5 : 1.5} />
                <text x={midX} y={y + 1} textAnchor="middle" dominantBaseline="middle"
                  fontSize={8} fill={flipped ? '#000' : TANNER_COLORS.xStab} fontFamily="monospace" fontWeight="bold">
                  {label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {(xErrors.size > 0 || zErrors.size > 0) && (
        <div className="flex gap-2 items-center">
          <button onClick={() => { setXErrors(new Set()); setZErrors(new Set()); }}
            className="px-2 py-1 text-[10px] rounded bg-gray-800 text-gray-400 hover:text-red-400">Clear all</button>
          <span className="text-[10px] text-gray-600">
            X: {xErrors.size > 0 ? [...xErrors].sort((a, b) => a - b).map((q) => `q${q}`).join(', ') : 'none'}
            {' | '}Z: {zErrors.size > 0 ? [...zErrors].sort((a, b) => a - b).map((q) => `q${q}`).join(', ') : 'none'}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Check Matrix ──

export function CheckMatrixSection({ code }: { code: StabilizerCode }) {
  const n = code.qubits.length;
  const qubitIds = useMemo(() => code.qubits.map((q) => q.id), [code.qubits]);

  const Hx = useMemo(() => buildParityMatrix(code.stabilizers, qubitIds, 'X'), [code.stabilizers, qubitIds]);
  const Hz = useMemo(() => buildParityMatrix(code.stabilizers, qubitIds, 'Z'), [code.stabilizers, qubitIds]);
  const rankHx = useMemo(() => Hx.length > 0 ? rowEchelon(Hx, n).rank : 0, [Hx, n]);
  const rankHz = useMemo(() => Hz.length > 0 ? rowEchelon(Hz, n).rank : 0, [Hz, n]);

  const renderMatrix = (label: string, type: 'X' | 'Z', mat: BitMatrix, rank: number, color: string) => {
    if (mat.length === 0) return <p className="text-gray-600 text-[10px]">No {label} stabilizers.</p>;
    const stabs = code.stabilizers.filter((s) => s.type === type);
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold" style={{ color }}>{label}</span>
          <span className="text-[10px] text-gray-500">{mat.length} x {n}, rank = {rank}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="border-collapse font-mono text-[10px]">
            <thead>
              <tr>
                <th className="px-1 text-gray-600"></th>
                {Array.from({ length: n }, (_, i) => (
                  <th key={i} className="px-1 text-gray-500 font-normal">q{i}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mat.map((row, ri) => (
                <tr key={ri} className="hover:bg-gray-800/40">
                  <td className="px-1 text-gray-500 text-right whitespace-nowrap">{stabs[ri]?.name || `${type}${ri}`}</td>
                  {row.map((v, ci) => (
                    <td key={ci} className={`px-1.5 py-0.5 text-center ${v ? 'font-bold' : 'text-gray-700'}`}
                      style={v ? { color } : undefined}>
                      {v ? '1' : '0'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const cssValid = useMemo(() => {
    if (Hx.length === 0 || Hz.length === 0) return true;
    for (const xRow of Hx) {
      for (const zRow of Hz) {
        let dot = 0;
        for (let c = 0; c < n; c++) { if (xRow[c] && zRow[c]) dot ^= 1; }
        if (dot !== 0) return false;
      }
    }
    return true;
  }, [Hx, Hz, n]);

  if (n === 0) return null;

  return (
    <div className="space-y-3">
      <div className={`px-2 py-1 rounded text-[10px] font-semibold ${cssValid ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}>
        H_X * H_Z^T = 0: {cssValid ? 'SATISFIED' : 'VIOLATED'}
      </div>
      {renderMatrix('H_X', 'X', Hx, rankHx, '#8b5cf6')}
      <div className="border-t border-gray-800" />
      {renderMatrix('H_Z', 'Z', Hz, rankHz, '#3b82f6')}
    </div>
  );
}

// ── Code Parameters [[n,k,d]] ──

export function CodeParamsSection({ code }: { code: StabilizerCode }) {
  const [claimedD, setClaimedD] = useState('');
  const [verifyResult, setVerifyResult] = useState<{
    n: number; k: number; dX: number; dZ: number; d: number; feasible: boolean;
  } | null>(null);
  const [computing, setComputing] = useState(false);

  const n = code.qubits.length;
  const qubitIds = useMemo(() => code.qubits.map((q) => q.id), [code.qubits]);
  const Hx = useMemo(() => buildParityMatrix(code.stabilizers, qubitIds, 'X'), [code.stabilizers, qubitIds]);
  const Hz = useMemo(() => buildParityMatrix(code.stabilizers, qubitIds, 'Z'), [code.stabilizers, qubitIds]);
  const rankHx = useMemo(() => Hx.length > 0 ? rowEchelon(Hx, n).rank : 0, [Hx, n]);
  const rankHz = useMemo(() => Hz.length > 0 ? rowEchelon(Hz, n).rank : 0, [Hz, n]);
  const k = n - rankHx - rankHz;

  const handleVerify = useCallback(() => {
    setComputing(true);
    setTimeout(() => {
      const result = computeCodeDistance(code);
      setVerifyResult(result);
      setComputing(false);
    }, 10);
  }, [code]);

  if (n === 0) return null;

  const claimedDNum = parseInt(claimedD, 10);
  const dMatch = verifyResult && !isNaN(claimedDNum) && verifyResult.d === claimedDNum;
  const dMismatch = verifyResult && !isNaN(claimedDNum) && verifyResult.feasible && verifyResult.d !== claimedDNum;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="bg-gray-800 rounded px-2 py-1 text-center">
          <div className="text-[9px] text-gray-500">n</div>
          <div className="text-sm font-bold font-mono text-white">{n}</div>
        </div>
        <div className="bg-gray-800 rounded px-2 py-1 text-center">
          <div className="text-[9px] text-gray-500">k</div>
          <div className="text-sm font-bold font-mono text-white">{k}</div>
        </div>
        <div className="bg-gray-800 rounded px-2 py-1 text-center">
          <div className="text-[9px] text-gray-500">rank H_X</div>
          <div className="text-xs font-bold font-mono text-purple-400">{rankHx}</div>
        </div>
        <div className="bg-gray-800 rounded px-2 py-1 text-center">
          <div className="text-[9px] text-gray-500">rank H_Z</div>
          <div className="text-xs font-bold font-mono text-blue-400">{rankHz}</div>
        </div>
      </div>

      <div className="text-[9px] text-gray-500 font-mono">
        k = {n} - {rankHx} - {rankHz} = {k}
      </div>

      <div className="border border-gray-800 rounded p-2 space-y-2">
        <div className="flex items-center gap-2">
          <label className="text-[10px] text-gray-500">d =</label>
          <input type="number" min={1} value={claimedD}
            onChange={(e) => setClaimedD(e.target.value)}
            className="w-14 px-1.5 py-0.5 text-xs bg-gray-800 border border-gray-700 rounded text-white font-mono" />
          <button onClick={handleVerify} disabled={computing}
            className={`px-2 py-0.5 text-[10px] rounded font-semibold transition-colors ${
              computing ? 'bg-gray-700 text-gray-500 cursor-wait' : 'bg-indigo-600 hover:bg-indigo-500 text-white'
            }`}>
            {computing ? 'Computing...' : 'Compute d'}
          </button>
        </div>

        {verifyResult && (
          <div className="space-y-1.5">
            {!verifyResult.feasible ? (
              <div className="bg-yellow-900/30 text-yellow-400 rounded px-2 py-1 text-[9px] font-semibold">
                Too large for brute-force (kernel dim &gt; 20).
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-500">d_X={verifyResult.dX === Infinity ? '∞' : verifyResult.dX}</span>
                  <span className="text-[10px] text-gray-500">d_Z={verifyResult.dZ === Infinity ? '∞' : verifyResult.dZ}</span>
                  <span className="text-[10px] text-white font-bold">d={verifyResult.d === Infinity ? '∞' : verifyResult.d}</span>
                </div>
                <div className={`rounded px-2 py-1 text-[10px] font-bold text-center ${
                  dMatch ? 'bg-green-900/40 text-green-400' : dMismatch ? 'bg-red-900/40 text-red-400' : 'bg-gray-800 text-gray-300'
                }`}>
                  {!isNaN(claimedDNum) ? (
                    dMatch
                      ? `[[${n}, ${k}, ${verifyResult.d}]] VERIFIED`
                      : `Claimed d=${claimedDNum}, actual d=${verifyResult.d}`
                  ) : (
                    `[[${n}, ${k}, ${verifyResult.d}]]`
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
